import {
  Component,
  input,
  OnInit,
  output,
  signal,
  TemplateRef,
  viewChild
} from '@angular/core';

import { CommonModule } from '@angular/common';
import { take } from 'rxjs';
import { SondermindEvent, SondermindEvents } from '@sondermind/analytics';
import {
  ISelfPayRateItem,
  SelfPayRateType,
  SelfPayRatesHttpService,
  SelfPayVisitType
} from '@sondermind/data-access/client-insurance';
import { Logger } from '@sondermind/datadog-logging';
import { IrisButtonContainerAlignments, IrisButtonModule, IrisButtonTypes } from '@sondermind/iris/button';
import { IconColors, IconSizes, IrisIconComponent } from '@sondermind/iris/icon';
import { IrisSpinnerComponent } from '@sondermind/iris/spinner';
import { SegmentService } from '@sondermind/segment';

import { SelfPayCostEstimateDetailedComponent } from '../self-pay-cost-estimate-detailed/self-pay-cost-estimate-detailed.component';
import { ISelfPayFeaturedEstimate } from '../../models/cost-estimation.model';

const FEATURED_LABEL_TALK_THERAPY_INTAKE = 'Talk therapy\n60 minutes intake';
const FEATURED_LABEL_PSYCHIATRY_INTAKE = 'Medication management\nInitial evaluation';

@Component({
  selector: 'client-self-pay-cost-estimation',
  templateUrl: './self-pay-cost-estimation.component.html',
  styleUrls: ['./self-pay-cost-estimation.component.scss'],
  imports: [
    CommonModule,
    SelfPayCostEstimateDetailedComponent,
    IrisButtonModule,
    IrisIconComponent,
    IrisSpinnerComponent
  ]
})
export class SelfPayCostEstimationComponent implements OnInit {
  readonly fromDirectBooking = input<boolean>(false);
  readonly personaClientId = input.required<string | number>();
  readonly costEstimationReviewed = output<void>();

  /** Only used when fromDirectBooking is true: provider-specific rate lookup */
  readonly personaProviderId = input<number>();
  readonly isPsychiatrist = input<boolean>();
  readonly isTelehealthAppointment = input<boolean>();

  readonly actionButtonTemplate = viewChild.required<TemplateRef<unknown>>('actionButton');

  readonly featuredEstimates = signal<ISelfPayFeaturedEstimate[]>([]);
  readonly rates = signal<ISelfPayRateItem[]>([]);
  readonly ratesLoading = signal<boolean>(false);

  IrisButtonContainerAlignments = IrisButtonContainerAlignments;
  IrisButtonTypes = IrisButtonTypes;
  IrisIconSizes = IconSizes;
  IconColors = IconColors;

  constructor(
    private readonly logger: Logger,
    private readonly segmentService: SegmentService<SondermindEvents>,
    private readonly selfPayRatesHttp: SelfPayRatesHttpService
  ) {}

  ngOnInit(): void {
    if (this.fromDirectBooking()) {
      this.loadProviderSpecificRates();
    }

    this.ratesLoading.set(true);
    this.selfPayRatesHttp
      .getSelfPayRates$(SelfPayVisitType.TELEHEALTH)
      .pipe(take(1))
      .subscribe({
        next: (rates) => {
          this.ratesLoading.set(false);
          if (rates.length === 0) {
            this.reviewedCostEstimation();
            return;
          }
          this.rates.set(rates);

          if (!this.fromDirectBooking()) {
            const featured = this.buildNonSchedulingFeaturedEstimatesFromRates(rates);
            this.featuredEstimates.set(featured);
            this.trackSelfPayCostEstimateShown(featured);
          }
        },
        error: (err: unknown) => {
          this.logger.error('Self-pay rates failed to load', { err });
          this.ratesLoading.set(false);
          this.reviewedCostEstimation();
        }
      });
  }

  /**
   * Loads the single self-pay rate for the pending direct-booking appointment (provider + psychiatry + telehealth).
   * Only runs when fromDirectBooking is true and personaProviderId is set.
   */
  private loadProviderSpecificRates(): void {
    const personaProviderId = this.personaProviderId();
    if (personaProviderId == null || personaProviderId === undefined) {
      this.reviewedCostEstimation();
      return;
    }

    this.ratesLoading.set(true);

    this.selfPayRatesHttp
      .getSelfPayRatesForProvider$(
        personaProviderId,
        this.isPsychiatrist() ?? false,
        this.isTelehealthAppointment() ?? false
      )
      .pipe(take(1))
      .subscribe({
        next: (rate) => {
          this.ratesLoading.set(false);

          this.featuredEstimates.set([{ amount: rate.amount?.amount ?? 0 }]);
          this.trackSelfPayCostEstimateShown([{ amount: rate.amount?.amount ?? 0 }]);
        },
        error: (err: unknown) => {
          this.logger.error('Provider self-pay rate failed to load', { err });
          this.ratesLoading.set(false);
          this.reviewedCostEstimation();
        }
      });
  }

  /**
   * Build featured estimates from API rates: only TALK_THERAPY_INTAKE and PSYCHIATRY_INTAKE,
   * with fixed labels. For non-direct-booking context only. API always returns all 5 rate types.
   */
  private buildNonSchedulingFeaturedEstimatesFromRates(rates: ISelfPayRateItem[]): ISelfPayFeaturedEstimate[] {
    const talkTherapyIntake = rates.find((r) => r.type === SelfPayRateType.TALK_THERAPY_INTAKE);
    const psychiatryIntake = rates.find((r) => r.type === SelfPayRateType.PSYCHIATRY_INTAKE);

    if (!talkTherapyIntake?.amount || !psychiatryIntake?.amount) {
      return [];
    }

    return [
      { serviceLabel: FEATURED_LABEL_TALK_THERAPY_INTAKE, amount: talkTherapyIntake.amount.amount },
      { serviceLabel: FEATURED_LABEL_PSYCHIATRY_INTAKE, amount: psychiatryIntake.amount.amount }
    ];
  }

  reviewedCostEstimation(): void {
    this.costEstimationReviewed.emit();
  }

  private trackSelfPayCostEstimateShown(featuredEstimates: ISelfPayFeaturedEstimate[]): void {
    const isFromDirectBooking = this.fromDirectBooking();
    const payload: SondermindEvents[SondermindEvent.SelfPayCostEstimateShown] = {
      fromDirectBooking: isFromDirectBooking,
      featuredEstimates: featuredEstimates.map((e) => ({
        serviceLabel: e.serviceLabel ?? '',
        amount: e.amount
      })),
      personaClientId: String(this.personaClientId())
    };

    if (isFromDirectBooking) {
      payload.telehealth = this.isTelehealthAppointment() ?? false;
      payload.psychiatryIntake = this.isPsychiatrist() ?? false;
    }

    this.segmentService
      .track(
      SondermindEvent.SelfPayCostEstimateShown,
      payload
    ).catch(() => {});
  }
}
